import { IndustrySelect } from "@/components/campaign/basic/IndustrySelect";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { IndustryCode, industryCodes } from "@/data/industry-codes";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { ComparisonOperator, comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Form } from "react-aria-components";
import { FormProvider, useForm } from "react-hook-form";
import z from "zod";

export const formSchema = z
  .object({
    operator: comparisonOperators.enum,
    industries: z.array(z.string()),
    includeSubIndustries: z.boolean(),
  })
  .refine(({ industries }) => industries.length > 0, {
    message: "Vælg mindst én branche",
  });
export type IndustryFilterFormValues = z.infer<typeof formSchema>;

type SelectedIndustriesByCategory = Map<
  string,
  { title: string; ids: Map<string, string> }
>;

export function IndustryFilter({
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const industryFilter = queryFilters.value;

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      operator: (industryFilter?.operator as ComparisonOperator) || "Equal",
      industries: industryFilter?.values ?? [],
      includeSubIndustries: !!industryFilter?.fieldNames?.length,
    },
  });

  useReportDirty(methods.formState.isDirty, setIsDirty);

  const submit = methods.handleSubmit((data) => {
    update({
      value: {
        operator: data.operator,
        fieldNames: data.includeSubIndustries
          ? ["altindustry1code", "altindustry2code", "altindustry3code"]
          : undefined,
        values: data.industries,
      },
    });
  });

  const watchedIndustries = methods.watch("industries");

  function findRootCategory(
    targetId: string,
    industries: IndustryCode[] = industryCodes,
  ): IndustryCode | null {
    for (const rootIndustry of industries) {
      if (rootIndustry.id === targetId) return rootIndustry;

      const findInChildren = (node: IndustryCode): boolean => {
        if (node.id === targetId) return true;
        return node.children?.some(findInChildren) ?? false;
      };

      if (findInChildren(rootIndustry)) {
        return rootIndustry;
      }
    }
    return null;
  }

  function findNameOfCategory(
    targetId: string,
    industries: IndustryCode[] = industryCodes,
  ): string | null {
    for (const rootIndustry of industries) {
      if (rootIndustry.id === targetId) return rootIndustry.name;

      const findInChildren = (node: IndustryCode): string | null => {
        if (node.id === targetId) return node.name;
        if (!node.children) return null;
        for (const child of node.children) {
          const result = findInChildren(child);
          if (result) return result;
        }
        return null;
      };

      const nameInChildren = findInChildren(rootIndustry);
      if (nameInChildren) {
        return nameInChildren;
      }
    }
    return null;
  }

  function groupIdsByCategory(ids: string[]): SelectedIndustriesByCategory {
    const grouped: SelectedIndustriesByCategory = new Map();

    for (const id of ids) {
      const name = findNameOfCategory(id);
      const root = findRootCategory(id);

      if (!root) continue;

      const existing = grouped.get(root.id);
      if (existing) {
        existing.ids.set(id, name ?? "");
      } else {
        grouped.set(root.id, {
          title: root.name,
          ids: new Map([[id, name ?? ""]]),
        });
      }
    }

    return grouped;
  }

  const [selectedIndustries, setSelectedIndustries] =
    useState<SelectedIndustriesByCategory>(() =>
      groupIdsByCategory(watchedIndustries),
    );

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={submit}
        className="flex size-full flex-col items-center justify-between gap-y-10"
        id={formId}
      >
        <div className="flex w-full shrink grow flex-col items-center gap-y-9">
          <SegmentHeader
            title="Branche"
            description="Virksomhedens primære branche, der definerer deres forretningsområde."
          />
          <div className="flex w-full flex-col items-center gap-y-4">
            <IndustrySelect
              selectedIndustries={selectedIndustries}
              setSelectedIndustries={setSelectedIndustries}
            />
          </div>
        </div>
      </Form>
    </FormProvider>
  );
}
